import { useMutation } from '@tanstack/react-query';
import { configApi } from '../../configApi';
import { FieldValues } from 'react-hook-form';
import {
  appendEconomicDocuments,
  getExemptionFileRoles,
  getExemptionType,
  isBoneMarrowExemption,
  isCadunicoExemption,
  isEconomicExemption,
} from './economicHyposufficiency';

interface IMutationCreateRegistration {
  data: FieldValues;
  exemptionId?: string;
}

const CreateExemption = async ({ data, exemptionId }: IMutationCreateRegistration) => {
  const listToCreate = new FormData();
  const typeExemption = getExemptionType(data.type_exemption);
  if (!typeExemption) throw new Error('Selecione o tipo de isenção.');

  if (data?.selective_process_id) {
    listToCreate.append('selective_process', data?.selective_process_id);
  }

  if (isEconomicExemption(typeExemption.name)) {
    // O subtype é opcional para hipossuficiência econômica. FormData.append
    // converteria undefined para a string "undefined", que a API tenta validar como UUID.
    if (typeof data.subtype === 'string' && data.subtype.trim()) {
      listToCreate.append('subtype', data.subtype);
    }
    appendEconomicDocuments(listToCreate, data.documents ?? []);
  } else if (data?.attachmentExemption?.length > 0) {
    const fileRoles = getExemptionFileRoles(typeExemption.name, data.attachmentExemption);
    for (let index = 0; index < data.attachmentExemption.length; index++) {
      listToCreate.append('files', data.attachmentExemption[index]);
      const role = fileRoles[index];
      if (role) listToCreate.append('file_roles', role);
    }
  }

  if (exemptionId) {
    (data.removedExemptionAttachmentIds ?? []).forEach((id: string) => {
      listToCreate.append('remove_attachment_ids', id);
    });
  }

  if (data?.type_exemption) {
    listToCreate.append('type', typeExemption.id);
  }

  if (isCadunicoExemption(typeExemption.name)) {
    if (data?.nis) {
      listToCreate.append('nis', data.nis);
    }

    if (data?.mother_name) {
      listToCreate.append('mother_name', data.mother_name);
    }

    if (data?.document_number) {
      listToCreate.append('document_number', data.document_number);
    }

    if (data?.document_issuing_body) {
      listToCreate.append('document_issuing_body', data.document_issuing_body);
    }

    if (data?.document_issuing_body_uf) {
      // exemptionObject.document_issuing_body_uf = data.document_issuing_body_uf;
      listToCreate.append('document_issuing_body_uf', data.document_issuing_body_uf);
    }
  }

  if (isBoneMarrowExemption(typeExemption.name)) {
    if (data?.redome_code) {
      listToCreate.append('redome_code', data.redome_code);
    }
  }

  if (data?.is_declaration_veracity !== undefined) {
    listToCreate.append('is_declaration_veracity', data.is_declaration_veracity);
  }

  const response = exemptionId
    ? await configApi.patch(`exemption/candidate/update/${exemptionId}/`, listToCreate, {
        params: { selective_process_id: data.selective_process_id },
      })
    : await configApi.post(`exemption/candidate/create/`, listToCreate);

  return response.data;
};

export const useCreateExemption = () => {
  return useMutation({
    mutationKey: ['create_exemption'],
    mutationFn: (payload: IMutationCreateRegistration) => CreateExemption(payload),
  });
};
